import styles from './index.module.scss'
import PictureResultItem from '../../components/PictureResultItem'
import PictureViewModal from '../../components/PictureViewModal'
import React, { forwardRef, useContext, useEffect, useRef, useState, useImperativeHandle } from 'react'
import { useProjectGroupedTasks } from '@view/page_v3/hooks/ProjectDetailSort'
import ProjectDetailGroupsTitle from '@view/page_v3/components/ProjectDetailGroupsTitle'
import { DISPLAY_TYPE, IMAGE_TYPE, SMART_DETAIL_COMP_LABEL_MAP } from '@utils/CONST'
import { AppContext } from '@utils/AppContext'
import { Button, message, Modal } from 'antd'
import { workflowOfLoadTaskInfo, workflowOfReGenerate } from '@utils/workflowOfBuildTaskV2'
import { useNavigate } from 'react-router-dom'
import { GenerateContext } from '../../hooks/GenerateContext'
import Iconfont from '@utils/Iconfont'
import API from '@api/api'
import MaterialEditModal from '../SmartDetailPagePreview/MaterialEditModal'

const GenerateResults = forwardRef((props, ref) => {
    useImperativeHandle(ref, () => ({
        fetchProjectDetail,
    }))

    const modalRef = useRef()
    const { projectId, className, style, onTasksChange, hideFooter } = props
    const { setGlobalTaskReadFlag } = useContext(AppContext)
    const { setProjectId, imageType } = useContext(GenerateContext)
    const navigate = useNavigate()

    const { groupedTasks, rawTasks, fetchProjectDetail } = useProjectGroupedTasks(projectId)

    const [recreateLoading, setRecreateLoading] = useState({})
    const createAgain = async (item1, groupIndex) => {
        const succeededItem = item1.find(item => item.status === 'succeeded') || item1.find(item => item.status == 'failed')
        if (succeededItem) {
            try {
                setRecreateLoading(prev => ({ ...prev, [groupIndex]: true }))
                const taskID = succeededItem.taskId
                const res = await workflowOfReGenerate(succeededItem.imageType, taskID)
                if (!res) return
                const { disabled, pathname, options } = res
                if (disabled) {
                    message.warning('版本更新，请重新编辑后再重新生成')
                    navigate(pathname, options)
                } else {
                    message.success('生成中，请稍后！')
                    setGlobalTaskReadFlag(prev => !prev)
                    fetchProjectDetail()
                }
            } catch (err) {
                message.warning(err.message)
            } finally {
                setRecreateLoading(prev => ({ ...prev, [groupIndex]: false }))
            }
        } else {
            message.warning('请先等待生成成功后再重新生成')
        }
    }

    const [rebuildLoading, setRebuildLoading] = useState({})
    const rebuildClick = async (item1, groupIndex, commidityOldDataRebuild) => {
        const succeededItem = item1.find(item => item.status === 'succeeded') || item1.find(item => item.status === 'failed')
        if (succeededItem) {
            try {
                setRebuildLoading(prev => ({ ...prev, [groupIndex]: true }))
                const res = await workflowOfLoadTaskInfo(succeededItem.imageType, succeededItem.taskId, commidityOldDataRebuild)
                if (!res) return
                const { pathname, options } = res
                navigate(pathname, options)
            } catch (err) {
                message.warning(err.message)
            } finally {
                setRebuildLoading(prev => ({ ...prev, [groupIndex]: false }))
            }
        } else {
            message.warning('生成中，请等待完成后再编辑')
        }
    }

    const [pickLoading, setPickLoading] = useState({})
    const handlePickImage = async (e, currentItem) => {
        e.stopPropagation()
        if (!currentItem?.taskId || currentItem?.status !== 'succeeded') return
        try {
            setPickLoading(prev => ({ ...prev, [currentItem.taskId]: true }))
            const res = await API.pickImage({
                taskIds: [currentItem.taskId]
            })
            if (res?.code !== 0) throw new Error(res?.message || '选图失败')
            fetchProjectDetail()
        } catch (err) {
            message.warning(err.message || '选图失败')
        } finally {
            setPickLoading(prev => ({ ...prev, [currentItem.taskId]: false }))
        }
    }

    const [isModalOpen, setIsModalOpen] = useState(false)
    const [item, setItem] = useState()
    const [taskId, setTaskId] = useState()
    const [modalOpenFlag, setModalOpenFlag] = useState(false)
    const [materialModalOpen, setMaterialModalOpen] = useState(false)
    const [materialEditItem, setMaterialEditItem] = useState(null)
    const [materialGenerating, setMaterialGenerating] = useState(false)

    const openMaterialEditModal = (targetItem) => {
        if (!targetItem || targetItem?.status !== 'succeeded') return
        setMaterialEditItem(targetItem)
        setMaterialModalOpen(true)
    }

    useEffect(() => {
        if (!isModalOpen) {
            setTaskId()
            setItem()
        }
    }, [isModalOpen])

    const handleCLick = (currentItem) => {
        if (currentItem.status != 'succeeded') return
        setItem(currentItem)
        setTaskId(currentItem?.taskId)
    }

    useEffect(() => {
        if (item && taskId) {
            setIsModalOpen(true)
        }
    }, [item, taskId])

    useEffect(() => {
        if (onTasksChange) onTasksChange(groupedTasks || [])
    }, [groupedTasks, onTasksChange])

    const handleMaterialGenerate = async ({ desc, referImage, item: currentItem }) => {
        if (!currentItem?.taskId || !desc) return
        try {
            setMaterialGenerating(true)
            const res = await API.doEditDetailPage({
                taskId: currentItem.taskId,
                prompt: desc,
                refImage: referImage || '',
                meta: Number(currentItem?.comp ?? 0),
            })
            if (res?.code !== 0) throw new Error(res?.message || '生成失败')
            message.success('生成中，请稍后！')
            setMaterialModalOpen(false)
            setMaterialEditItem(null)
            fetchProjectDetail()
        } catch (err) {
            message.warning(err?.message || '生成失败')
        } finally {
            setMaterialGenerating(false)
        }
    }

    return (
        <>
            <div className={`${styles.GenerateResults} _beauty_scrollbar ${className || ''}`} style={style}>
                {groupedTasks.map((item1, index) => {
                    const currentImageType = item1?.[0]?.imageType
                    const isSmartDetailPage = currentImageType === IMAGE_TYPE.SMART_DETAILA_PAGE
                    const displayItems = isSmartDetailPage
                        ? (item1 || [])
                            .map((item, itemIndex) => ({ item, itemIndex }))
                            .sort((a, b) => {
                                const aSortIdx = Number(a?.item?.sortIdx)
                                const bSortIdx = Number(b?.item?.sortIdx)
                                const aValue = Number.isFinite(aSortIdx) ? aSortIdx : Number.MAX_SAFE_INTEGER
                                const bValue = Number.isFinite(bSortIdx) ? bSortIdx : Number.MAX_SAFE_INTEGER
                                if (aValue !== bValue) return aValue - bValue
                                return a.itemIndex - b.itemIndex
                            })
                            .map(({ item }) => item)
                        : (item1 || [])

                    return (
                        <React.Fragment key={index}>
                            <div className={styles.Title}>
                                <ProjectDetailGroupsTitle
                                    item={item1[0]}
                                    imageType={currentImageType}
                                />
                                <>
                                    <Button
                                        style={{ width: 120, height: 32, marginRight: 12 }}
                                        loading={rebuildLoading[index]}
                                        onClick={() => rebuildClick(item1, index)}
                                    >
                                        重新编辑
                                    </Button>
                                    {!isSmartDetailPage && (
                                        <Button
                                            style={{ width: 120, height: 32 }}
                                            loading={recreateLoading[index]}
                                            onClick={() => {
                                                if (currentImageType === IMAGE_TYPE.COMMODITY && item1?.[0]?.version !== 'V3.8.20251127') {
                                                    rebuildClick(item1, index, true)
                                                } else {
                                                    createAgain(item1, index)
                                                }
                                            }}
                                        >
                                            再次生成
                                        </Button>
                                    )}
                                </>
                            </div>
                            {displayItems.map((item2) => (
                                <div
                                    key={item2.taskId}
                                    className={`${styles.ContentItem} ${isSmartDetailPage ? styles.SmartDetailContentItem : ''}`}
                                >
                                    <PictureResultItem
                                        displayType={DISPLAY_TYPE.GENERATE}
                                        setProjectId={setProjectId}
                                        item={item2}
                                        imageType={item2?.imageType}
                                        hideBottomMenu={isSmartDetailPage}
                                        handleCLick={() => handleCLick(item2)}
                                        isSelected={false}
                                        selectedList={[]}
                                        rawTasks={rawTasks}
                                        selectCurrentItem={null}
                                        fetchProjectDetail={fetchProjectDetail}
                                    />
                                    {isSmartDetailPage && item2?.status === 'succeeded' && !item2?.refTemplate && SMART_DETAIL_COMP_LABEL_MAP?.[Number(item2?.comp)] && (
                                        <div className={styles.CompTag}>
                                            {SMART_DETAIL_COMP_LABEL_MAP[Number(item2?.comp)]}
                                        </div>
                                    )}
                                    {isSmartDetailPage && item2?.status === 'succeeded' && (
                                        <p
                                            className={`${styles.ChooseBtn} ${item2?.picked ? styles.ChooseBtnActive : ''}`}
                                            onClick={(e) => handlePickImage(e, item2)}
                                        >
                                            <Iconfont className={styles.Icon} type="icon-imgOutlined" />
                                            {item2?.picked ? '已选中' : '选这张'}
                                        </p>
                                    )}
                                    {isSmartDetailPage && item2?.status === 'succeeded' && (
                                        <span
                                            className={styles.MaterialEditTrigger}
                                            onClick={(e) => {
                                                e.stopPropagation()
                                                openMaterialEditModal(item2)
                                            }}
                                        >
                                            <Iconfont type="icon-Material-EditOutlined" />
                                        </span>
                                    )}
                                </div>
                            ))}
                            <p style={{ marginBottom: '50px', width: '100%' }}></p>
                        </React.Fragment>
                    )
                })}
            </div>
            {!hideFooter && (
                <p className={`${styles.Footer} ${imageType == IMAGE_TYPE.FUSION_WEAR ? styles.FooterFusionWear : ''} ${imageType == IMAGE_TYPE.CREATIVE_IMAGE_GEN ? styles.FooterCentered : ''}`}>
                    以上内容由人工智能模型生成，不代表平台的观点、立场或态度，请合理友善地使用生成内容
                </p>
            )}
            <Modal
                open={isModalOpen}
                onOk={() => setIsModalOpen(false)}
                footer={null}
                maskClosable={true}
                width="100%"
                style={{ top: 0, padding: 0, margin: 0, maxWidth: '100vw', height: '100vh' }}
                bodyStyle={{ height: '100vh', overflow: 'hidden' }}
                destroyOnHidden
                onCancel={() => setIsModalOpen(false)}
                className={styles.PictureViewModal}
                afterOpenChange={(open) => setModalOpenFlag(open)}
                closeIcon={false}
            >
                <PictureViewModal
                    modalOpenFlag={modalOpenFlag}
                    displayType={DISPLAY_TYPE.GENERATE}
                    setProjectId={setProjectId}
                    ref={modalRef}
                    taskId={taskId}
                    setTaskId={setTaskId}
                    isModalOpen={isModalOpen}
                    setIsModalOpen={setIsModalOpen}
                    item={item}
                    setItem={setItem}
                    groupedTasks={groupedTasks}
                    fetchProjectDetail={fetchProjectDetail}
                    onOpenMaterialEdit={openMaterialEditModal}
                />
            </Modal>
            <MaterialEditModal
                open={materialModalOpen}
                onCancel={() => {
                    setMaterialModalOpen(false)
                    setMaterialEditItem(null)
                }}
                item={materialEditItem}
                onGenerate={handleMaterialGenerate}
                generating={materialGenerating}
            />
        </>

    )
})

export default GenerateResults
